import { useEffect, useRef, useState, type JSX, type MutableRefObject, type RefObject } from "react";
import { X } from "lucide-react";
import { AppEntry } from "./AppEntry";
import { CollapseHandle } from "./CollapseHandle";
import { useCollapsedNavGroups } from "./hooks/use-collapsed-nav-groups";
import { cn } from "./lib/utils";
import { NavGroup } from "./NavGroup";
import {
  partitionNavEntries,
  resolveWorkspaceApps,
  toNavGroups
} from "./nav-model";
import { ProfileMenu } from "./ProfileMenu";
import { ScrollShadow } from "./ScrollShadow";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import type { NavEntry, RegistryEntry, Workspace } from "../types";
import { mergeNavEntries, navManifestGroupsToEntries } from "../navEntries";

export interface SidebarProps {
  isMobile: boolean;
  isAuthenticated: boolean;
  authEnabled: boolean;
  showAppChrome: boolean;
  apps: RegistryEntry[];
  currentEntryId: string | null;
  sections: NavEntry[];
  activePathname: string;
  identityEmail: string | null;
  tenantName: string | null;
  collapsed: boolean;
  mobileNavOpen: boolean;
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  expandedAppId: string | null;
  canChangeTenant: boolean;
  resolveHref: (route: string) => string;
  onNavigate: (to: string) => void;
  onToggleCollapsed: () => void;
  onMobileClose: () => void;
  onWorkspaceChange: (workspaceId: string) => void;
  onExpandedAppChange: (appId: string | null) => void;
  onSignOut: () => void;
  onChangeTenant: () => void;
}

interface SidebarAppEntriesProps {
  appSwitcherRef: RefObject<HTMLDivElement>;
  appEntryRefs: MutableRefObject<Map<string, HTMLDivElement>>;
  workspaceApps: ReturnType<typeof resolveWorkspaceApps>;
  currentEntryId: string | null;
  expandedId: string | null;
  collapsed: boolean;
  mainGroups: ReturnType<typeof toNavGroups>;
  activePathname: string;
  isGroupCollapsed: (groupId: string) => boolean;
  toggleGroup: (groupId: string) => void;
  navigateAndClose: (route: string) => void;
  onExpandedAppChange: (appId: string | null) => void;
}

function SidebarAppEntries({
  appSwitcherRef,
  appEntryRefs,
  workspaceApps,
  currentEntryId,
  expandedId,
  collapsed,
  mainGroups,
  activePathname,
  isGroupCollapsed,
  toggleGroup,
  navigateAndClose,
  onExpandedAppChange
}: SidebarAppEntriesProps): JSX.Element {
  return (
    <div ref={appSwitcherRef} data-platform-app-switcher="" className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
      {workspaceApps.length === 0 && !collapsed ? (
        <p className="px-3 py-6 text-center text-sm text-sidebar-text-muted">No apps in this workspace yet.</p>
      ) : (
        workspaceApps.map((app) => {
          const isActive = app.id === currentEntryId;
          const isExpanded = app.id === expandedId;
          return (
            <div key={app.id} ref={(element) => {
              if (element) appEntryRefs.current.set(app.id, element);
              else appEntryRefs.current.delete(app.id);
            }} className={cn("flex flex-col", isExpanded ? "mb-1 min-h-10 flex-[0_1_auto]" : "mb-1 shrink-0")}>
              <div className="shrink-0">
                <AppEntry id={app.id} label={app.label} icon={app.icon} color={app.color} accent={app.accent} href={app.href} route={app.route} active={isActive} collapsed={collapsed} disabled={app.disabled} onExpand={onExpandedAppChange} />
              </div>
              {isExpanded && (
                <div aria-hidden={!isExpanded} className="grid min-h-0 flex-[1_1_auto] grid-rows-[minmax(0,1fr)] opacity-100 transition-[grid-template-rows,opacity] duration-300 ease-out">
                  <div className="flex h-full min-h-0 flex-col overflow-hidden">
                    <ScrollShadow className="h-0 min-h-0 flex-1">
                      <div data-platform-side-nav="" className={cn("flex flex-col gap-1", collapsed && "items-center")}>
                        {mainGroups.map((group) => (
                          <NavGroup key={group.id} group={group} activePathname={activePathname} collapsed={collapsed} expanded={!isGroupCollapsed(group.id)} onToggle={() => toggleGroup(group.id)} onNavigate={navigateAndClose} />
                        ))}
                      </div>
                    </ScrollShadow>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

/**
 * The shell's primary navigation column. Apps are grouped by workspace - a
 * visual mockup today, since only Internal Development has live apps; the
 * active app's nav sections expand beneath it, because nav entries are
 * registered at runtime by the mounted app. Collapses to an icon rail, with
 * tooltips carrying the labels.
 */
export function Sidebar({
  isMobile,
  isAuthenticated,
  authEnabled,
  showAppChrome,
  apps,
  currentEntryId,
  sections,
  activePathname,
  identityEmail,
  tenantName,
  collapsed,
  mobileNavOpen,
  workspaces,
  activeWorkspaceId,
  expandedAppId,
  canChangeTenant,
  resolveHref,
  onNavigate,
  onToggleCollapsed,
  onMobileClose,
  onWorkspaceChange,
  onExpandedAppChange,
  onSignOut,
  onChangeTenant
}: SidebarProps): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const appSwitcherRef = useRef<HTMLDivElement>(null);
  const appEntryRefs = useRef(new Map<string, HTMLDivElement>());

  useEffect(() => {
    if (!showAppChrome) {
      setSidebarExpanded(false);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setSidebarExpanded(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [showAppChrome]);

  const workspaceId = activeWorkspaceId ?? workspaces[0]?.id ?? "";
  const activeWorkspace = workspaces.find((entry) => entry.id === workspaceId) ?? workspaces[0];
  const expandedId = expandedAppId ?? currentEntryId;
  const expandedApp = apps.find((entry) => entry.id === expandedId);
  const resolvedSections = mergeNavEntries(
    expandedApp ? navManifestGroupsToEntries(expandedApp.navGroups) : [],
    expandedId === currentEntryId ? sections : []
  );
  const { main, bottom } = partitionNavEntries(resolvedSections);
  const mainGroups = toNavGroups(main, resolveHref);
  const bottomGroups = toNavGroups(bottom, resolveHref);
  const workspaceApps = resolveWorkspaceApps(
    workspaceId,
    apps,
    resolveHref,
    activeWorkspace?.apps ?? apps
  );

  useEffect(() => {
    if (!expandedId) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const appSwitcher = appSwitcherRef.current;
      const appEntry = appEntryRefs.current.get(expandedId);
      if (!appSwitcher || !appEntry) {
        return;
      }

      const switcherRect = appSwitcher.getBoundingClientRect();
      const entryRect = appEntry.getBoundingClientRect();
      const lowerViewportHalf = switcherRect.top + switcherRect.height / 2;
      if (entryRect.bottom > lowerViewportHalf) {
        appSwitcher.scrollTo({
          top: appSwitcher.scrollTop + entryRect.top - switcherRect.top,
          behavior: "smooth"
        });
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [expandedId]);

  // Group ids are passed so stale collapsed entries get pruned on write.
  const allGroupIds = [...mainGroups, ...bottomGroups].map((group) => group.id);
  const { isGroupCollapsed, toggleGroup } = useCollapsedNavGroups(currentEntryId, allGroupIds);
  const navigateAndClose = (route: string): void => {
    onMobileClose();
    onNavigate(route);
  };
  const changeWorkspaceAndClose = (workspaceId: string): void => {
    onMobileClose();
    onWorkspaceChange(workspaceId);
  };

  let sidebarWidth = "calc(var(--tc-space-5xl) * 3 + var(--tc-space-m) + var(--tc-space-2xs))";
  if (isMobile) sidebarWidth = "100%";
  else if (collapsed) sidebarWidth = "calc(var(--tc-space-3xl) + var(--tc-space-xs) + var(--tc-space-2xs))";

  return (
    <nav
      aria-label="Primary"
      data-platform-chrome=""
      data-platform-sidebar-theme=""
      data-sidebar-collapsed={collapsed ? "true" : "false"}
      data-sidebar-expanded={sidebarExpanded ? "true" : "false"}
      data-mobile-open={isMobile ? String(mobileNavOpen) : undefined}
      aria-hidden={isMobile ? !mobileNavOpen : undefined}
      hidden={!showAppChrome}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ width: sidebarWidth }}
      className={cn(
        // No `overflow-hidden` here: the collapse handle deliberately translates
        // past the sidebar's right edge into the gutter, and clipping it here is
        // what made the toggle look like the content panel was covering it.
        // box-sizing comes from the scoped [data-platform-chrome] rule in
        // shell-chrome.css, not a `box-border` utility here - this element
        // carries that attribute itself, so the rule already applies to it.
        "group/sidebar relative flex h-full shrink-0 flex-col rounded-xl",
        isMobile ? "px-4 pb-4 pt-6" : "px-0 pb-2 pt-2",
        isMobile && "fixed inset-0 z-50 bg-sidebar-surface transition-[opacity,transform,visibility] duration-300 ease-out",
        isMobile && !mobileNavOpen && "pointer-events-none invisible -translate-x-full opacity-0",
        isMobile && "motion-reduce:transition-none",
        // Transparent: the sidebar sits directly on the shell canvas, like the
        // reference shell. The sidebar-* hover/active tokens are therefore tuned
        // against the canvas in every theme rather than a sidebar fill.
        "text-sidebar-text transition-[width,opacity,transform,visibility] duration-200"
      )}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {showAppChrome && workspaces.length > 0 ? (
        <div
          className={cn(
            "mb-4 flex shrink-0 items-center",
            !isMobile && "px-2",
            collapsed && "justify-center"
          )}
        >
          <WorkspaceSwitcher
            workspaces={workspaces}
            activeWorkspaceId={workspaceId}
            hovered={hovered}
            collapsed={collapsed}
            onSelectWorkspace={changeWorkspaceAndClose}
          />
        </div>
      ) : null}

      {showAppChrome ? (
        <SidebarAppEntries appSwitcherRef={appSwitcherRef} appEntryRefs={appEntryRefs} workspaceApps={workspaceApps} currentEntryId={currentEntryId} expandedId={expandedId} collapsed={collapsed} mainGroups={mainGroups} activePathname={activePathname} isGroupCollapsed={isGroupCollapsed} toggleGroup={toggleGroup} navigateAndClose={navigateAndClose} onExpandedAppChange={onExpandedAppChange} />
      ) : (
        <div className="flex-1" />
      )}

      {isAuthenticated ? (
        // shrink-0 keeps the account row pinned and fully visible: without it a
        // tall app/nav list steals its height and pushes it past the sidebar.
        <div
          className={cn(
            "mt-auto flex shrink-0 flex-col gap-1 pt-1 pb-0",
            collapsed && "items-center"
          )}
        >
          {bottomGroups.map((group) => (
            <NavGroup
              key={group.id}
              group={group}
              activePathname={activePathname}
              collapsed={collapsed}
              expanded={!isGroupCollapsed(group.id)}
              onToggle={() => toggleGroup(group.id)}
              onNavigate={navigateAndClose}
            />
          ))}
          <ProfileMenu
            collapsed={collapsed}
            identityEmail={identityEmail}
            tenantName={tenantName}
            authEnabled={authEnabled}
            canChangeTenant={canChangeTenant}
            onChangeTenant={() => {
              onMobileClose();
              onChangeTenant();
            }}
            onSignOut={onSignOut}
          />
        </div>
      ) : null}
      </div>

      {isMobile ? (
        <button
          type="button"
          aria-label="Close navigation"
          data-platform-mobile-close=""
          onClick={onMobileClose}
          className={cn(
            "absolute right-4 top-6 z-10 flex size-10 items-center justify-center rounded-lg border border-sidebar-border bg-sidebar-surface-hover text-sidebar-text shadow-card transition-[background-color,border-color,transform] duration-150 ease-out hover:border-sidebar-text-muted hover:bg-nav-active hover:scale-105 active:scale-95",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100"
          )}
        >
          <X className="size-5" strokeWidth={1.75} aria-hidden="true" />
        </button>
      ) : (
        <CollapseHandle
          collapsed={collapsed}
          onToggle={onToggleCollapsed}
          className={cn(
          // translate-x-1/2 is exactly half the handle's width, so it lands
          // centred on the sidebar edge and fills the 8px gutter without
          // reaching under the content panel. z-30 keeps it above the panel.
          "absolute top-1/2 right-0 z-30 -translate-y-1/2 translate-x-[calc(50%+var(--tc-space-2xs))]",
          "opacity-0 transition-opacity duration-150 group-hover/sidebar:opacity-100",
          "focus-visible:opacity-100"
          )}
        />
      )}
    </nav>
  );
}
